import React, { useState, useEffect } from 'react';
import { Calendar, Plus, X, Check, Edit2, Trash2, Search, Filter } from 'lucide-react';

const API_BASE = 'http://114.29.239.77:7057';

export default function CalendarApp() {
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [editingEvent, setEditingEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    category: '[System] Incident',
    date: '',
    activity: '',
    name: '',
    contact: '',
    done: false
  });

  const categories = [
    '[System] Incident',
    '[System] Request',
    '[Network] Issue',
    '[Hardware] Repair',
    '[Software] Update',
    'Meeting',
    'Other'
  ];

  // Fetch events
  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/Events`);
      const data = await response.json();
      setEvents(data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Create event
  const createEvent = async () => {
    try {
      const response = await fetch(`${API_BASE}/Events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        await fetchEvents();
        resetForm();
        setShowModal(false);
      }
    } catch (error) {
      console.error('Error creating event:', error);
    }
  };

  // Update event
  const updateEvent = async () => {
    try {
      const response = await fetch(`${API_BASE}/Events`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        await fetchEvents();
        resetForm();
        setShowModal(false);
        setEditingEvent(null);
      }
    } catch (error) {
      console.error('Error updating event:', error);
    }
  };

  // Delete event
  const deleteEvent = async (title) => {
    if (window.confirm(`Delete event "${title}"?`)) {
      try {
        const response = await fetch(`${API_BASE}/Events/${title}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          await fetchEvents();
        }
      } catch (error) {
        console.error('Error deleting event:', error);
      }
    }
  };

  // Toggle done status
  const toggleDone = async (event) => {
    try {
      const response = await fetch(`${API_BASE}/Events/done/${event.title}`, {
        method: 'POST'
      });
      if (response.ok) {
        await fetchEvents();
      }
    } catch (error) {
      console.error('Error toggling done:', error);
    }
  };

  // Mark activity done
  const markActivityDone = async (title) => {
    try {
      const response = await fetch(`${API_BASE}/Events/activity/${title}`, {
        method: 'POST'
      });
      if (response.ok) {
        await fetchEvents();
      }
    } catch (error) {
      console.error('Error marking activity done:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      category: '[System] Incident',
      date: '',
      activity: '',
      name: '',
      contact: '',
      done: false
    });
    setEditingEvent(null);
  };

  const openEditModal = (event) => {
    setFormData({
      title: event.title,
      category: event.category,
      date: event.date.split('T')[0] + 'T' + event.date.split('T')[1].substring(0, 5),
      activity: event.activity,
      name: event.name,
      contact: event.contact,
      done: event.done
    });
    setEditingEvent(event);
    setShowModal(true);
  };

  // Calendar logic
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    return events.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const filteredEvents = events.filter(event => {
    const matchesSearch = event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         event.activity.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         event.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || event.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                     'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const days = getDaysInMonth(currentDate);

  return (
    <div className="min-h-screen bg-[#0f1419]">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap');
        
        * {
          font-family: 'Manrope', sans-serif;
        }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes pop {
          0% { transform: scale(0.8); opacity: 0; }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); opacity: 1; }
        }
        
        .animate-slide-in {
          animation: slideIn 0.4s ease-out;
        }
        
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        
        .animate-pop {
          animation: pop 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }
        
        .glass-dark {
          background: rgba(31, 41, 55, 0.6);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(55, 65, 81, 0.5);
        }
        
        .event-dot {
          position: absolute;
          bottom: 4px;
          left: 50%;
          transform: translateX(-50%);
          width: 6px;
          height: 6px;
          border-radius: 50%;
        }
        
        .category-badge {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .hover-lift {
          transition: transform 0.2s, box-shadow 0.2s;
        }
        
        .hover-lift:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px -10px rgba(59, 130, 246, 0.3);
        }
      `}</style>

      {/* Header */}
      <div className="bg-[#1a1f2e] border-b border-gray-800 shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between animate-slide-in">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Calendar className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white tracking-tight">Calendar</h1>
                <p className="text-sm text-gray-400 font-medium">Manage your events effortlessly</p>
              </div>
            </div>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 hover:shadow-xl hover:scale-105 transition-all duration-200"
            >
              <Plus className="w-5 h-5" />
              New Event
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Calendar Section */}
          <div className="lg:col-span-2 space-y-6">
            <div className="glass-dark rounded-3xl p-8 shadow-xl animate-fade-in">
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-8">
                <button
                  onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))}
                  className="w-10 h-10 rounded-xl bg-gray-700 hover:bg-gray-600 text-white flex items-center justify-center transition-colors"
                >
                  ←
                </button>
                <h2 className="text-2xl font-bold text-white">
                  {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                </h2>
                <button
                  onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))}
                  className="w-10 h-10 rounded-xl bg-gray-700 hover:bg-gray-600 text-white flex items-center justify-center transition-colors"
                >
                  →
                </button>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-3">
                {dayNames.map(day => (
                  <div key={day} className="text-center font-bold text-sm text-gray-400 pb-3">
                    {day}
                  </div>
                ))}
                
                {days.map((day, index) => {
                  const dayEvents = day ? getEventsForDate(day) : [];
                  const isToday = day && day.toDateString() === new Date().toDateString();
                  const isSelected = selectedDate && day && day.toDateString() === selectedDate.toDateString();
                  
                  return (
                    <div
                      key={index}
                      onClick={() => day && setSelectedDate(day)}
                      className={`
                        relative aspect-square rounded-2xl flex items-center justify-center cursor-pointer transition-all duration-200
                        ${!day ? 'invisible' : ''}
                        ${isToday ? 'bg-blue-600 text-white font-bold shadow-lg shadow-blue-500/50 scale-105' : 
                          isSelected ? 'bg-blue-500/30 ring-2 ring-blue-500' :
                          dayEvents.length > 0 ? 'bg-gray-700 hover:bg-gray-600 text-white' :
                          'bg-gray-800/50 hover:bg-gray-700/50 text-gray-300'}
                        hover:scale-105 hover:shadow-md
                      `}
                    >
                      {day && (
                        <>
                          <span className={`text-sm ${isToday ? 'font-bold' : 'font-semibold'}`}>
                            {day.getDate()}
                          </span>
                          {dayEvents.length > 0 && !isToday && (
                            <span className="event-dot bg-blue-500"></span>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Events Sidebar */}
          <div className="space-y-6">
            {/* Search & Filter */}
            <div className="glass-dark rounded-3xl p-6 shadow-xl animate-fade-in">
              <div className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search events..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 rounded-xl bg-gray-800 border-2 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none transition-colors"
                  />
                </div>
                
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-gray-800 border-2 border-gray-700 text-white focus:border-blue-500 focus:outline-none transition-colors font-medium"
                >
                  <option value="all">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Events List */}
            <div className="glass-dark rounded-3xl p-6 shadow-xl max-h-[600px] overflow-y-auto animate-fade-in">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Filter className="w-5 h-5 text-blue-400" />
                {selectedDate ? `Events on ${selectedDate.toLocaleDateString()}` : 'All Events'}
                <span className="ml-auto text-sm bg-blue-600 text-white px-3 py-1 rounded-full">
                  {(selectedDate ? getEventsForDate(selectedDate) : filteredEvents).length}
                </span>
              </h3>
              
              <div className="space-y-3">
                {(selectedDate ? getEventsForDate(selectedDate) : filteredEvents).map((event, idx) => (
                  <div
                    key={idx}
                    className="bg-gray-800/80 rounded-2xl p-4 hover-lift border-2 border-gray-700 animate-pop"
                    style={{ animationDelay: `${idx * 0.05}s` }}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1">
                        <h4 className={`font-bold text-white mb-1 ${event.done ? 'line-through text-gray-500' : ''}`}>
                          {event.title}
                        </h4>
                        <span className="category-badge bg-blue-600/20 text-blue-400 border border-blue-500/30">
                          {event.category}
                        </span>
                      </div>
                      <button
                        onClick={() => toggleDone(event)}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                          event.done ? 'bg-green-600 text-white' : 'bg-gray-700 hover:bg-gray-600 text-gray-400'
                        }`}
                      >
                        {event.done && <Check className="w-5 h-5" />}
                      </button>
                    </div>
                    
                    <div className="space-y-1 text-sm text-gray-400 mb-3">
                      <p><strong className="text-gray-300">Activity:</strong> {event.activity}</p>
                      <p><strong className="text-gray-300">Date:</strong> {new Date(event.date).toLocaleString()}</p>
                      <p><strong className="text-gray-300">Contact:</strong> {event.name} ({event.contact})</p>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditModal(event)}
                        className="flex-1 py-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-500/30 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => markActivityDone(event.title)}
                        className="flex-1 py-2 bg-green-600/20 hover:bg-green-600/30 text-green-400 border border-green-500/30 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                      >
                        <Check className="w-4 h-4" />
                        Done
                      </button>
                      <button
                        onClick={() => deleteEvent(event.title)}
                        className="py-2 px-4 bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-500/30 rounded-lg font-semibold transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                
                {(selectedDate ? getEventsForDate(selectedDate) : filteredEvents).length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <Calendar className="w-16 h-16 mx-auto mb-3 opacity-30" />
                    <p className="font-medium">No events found</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6 z-50 animate-fade-in">
          <div className="bg-[#1a1f2e] rounded-3xl p-8 max-w-lg w-full shadow-2xl border border-gray-800 animate-pop">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">
                {editingEvent ? 'Edit Event' : 'Create New Event'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="w-10 h-10 rounded-xl bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl bg-gray-800 border-2 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none transition-colors"
                  placeholder="Event title"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl bg-gray-800 border-2 border-gray-700 text-white focus:border-blue-500 focus:outline-none transition-colors"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Date & Time</label>
                <input
                  type="datetime-local"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl bg-gray-800 border-2 border-gray-700 text-white focus:border-blue-500 focus:outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Activity</label>
                <textarea
                  value={formData.activity}
                  onChange={(e) => setFormData({...formData, activity: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl bg-gray-800 border-2 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none transition-colors resize-none"
                  rows="3"
                  placeholder="Describe the activity"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl bg-gray-800 border-2 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none transition-colors"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Contact</label>
                <input
                  type="text"
                  value={formData.contact}
                  onChange={(e) => setFormData({...formData, contact: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl bg-gray-800 border-2 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none transition-colors"
                  placeholder="Contact number"
                />
              </div>

              <button
                onClick={editingEvent ? updateEvent : createEvent}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-bold hover:shadow-xl hover:scale-105 transition-all duration-200"
              >
                {editingEvent ? 'Update Event' : 'Create Event'}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-2xl p-6 shadow-xl">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      )}
    </div>
  );
}